import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { PageHeader } from "@/app/PageHeader";
import { usePerformanceMilestone } from "@/lib/usePerformanceMilestone";
import { useAuth } from "@/features/auth/AuthContext";
import { hasLoadedOnce, useAsync } from "@/lib/useAsync";
import { useAutoRefresh } from "@/lib/useAutoRefresh";
import { useModalA11y } from "@/lib/useModalA11y";
import { DeviceRenameButton } from "../components/DeviceRenameButton";
import { getDeviceDisplayName } from "../adapters";
import {
  IconBroadcast,
  IconCopy,
  IconCpu,
  IconChevronRight,
  IconDownload,
  IconEdit,
  IconFilter,
  IconHash,
  IconLink,
  IconRefresh,
  IconShield,
  IconWifi,
  IconWrench,
} from "@/lib/icons";

import { claimDevice, getDeviceDashboard, unclaimDevice } from "../api";
import {
  buildTelemetryHealthSummary,
  formatDateTime,
  formatRelative,
  getFreshness,
  getFunctions,
  getSignalMetadata,
  isDeviceOnlineNow,
  normalizeMode,
  parseMetaJson,
  type TelemetryHealthSummary,
} from "../adapters";
import { ErrorState } from "../components/ErrorState";
import { LoadingState } from "../components/LoadingState";
import { describeAnalogDiagnostic, resolveAnalogDiagnostic } from "../analogDiagnostic";
import { describeModbusStatus, resolveModbusStatus } from "../modbusDiagnostic";
import { diagnosticFunctionKindLabel, diagnosticValueKindLabel } from "../diagnosticSignalMeaning";
import type {
  LatestDigitalInput,
  LatestPort,
  MetaFunctionEntry,
} from "../types";

const SYNC_FRESHNESS_MS = 120_000;

/**
 * Página técnica `/devices/:deviceId`.
 *
 * Layout do contrato `dashboard-operacional/device.jsx`:
 *  1. Header com nome + status + MQTT ID + ações (Resync / Reassociar / Configurar)
 *  2. Diagnóstico enxuto: MQTT, RSSI, IP e firmware
 *  3. Saúde da telemetria nas últimas 24h
 *  4. Sinais recebidos (tabela com Porta · Nome · Modo · s · acc · v · medido em)
 *  5. Configuração do gateway (funções declaradas no `metaJson`)
 *  6. Manutenção (operações sensíveis: device.id, MQTT ID, reassociar)
 */
export function DeviceDashboardPage() {
  const { deviceId = "" } = useParams<{ deviceId: string }>();
  const [searchParams] = useSearchParams();
  const defaultWorkspaceId =
    searchParams.get("workspaceId") ?? searchParams.get("workflowId") ?? undefined;
  const autoRefreshIntervalMs = 5000;

  const { state, reload } = useAsync(`devices/${deviceId}/dashboard`, (signal) =>
    getDeviceDashboard(deviceId, signal),
  );

  // Auto-refresh só dispara depois da primeira resposta — evita prender em
  // loading se o backend estiver lento na primeira chamada. Sem botão de
  // pausar visível: a reconciliação no `useAsync` evita re-renders quando o
  // JSON volta idêntico, e operações destrutivas (`reassociar`) usam suas
  // próprias confirmações.
  useAutoRefresh({
    // `hasLoadedOnce` (não `status === "success"`): mantém o polling vivo
    // através de erros transitórios para a tela não congelar. Ver useAsync.ts.
    enabled: hasLoadedOnce(state),
    intervalMs: autoRefreshIntervalMs,
    onRefresh: reload,
  });

  const data = state.data;
  usePerformanceMilestone("diagnostic_commit", data !== null);
  useEffect(() => {
    window.addEventListener("ghm:devices-updated", reload);
    return () => window.removeEventListener("ghm:devices-updated", reload);
  }, [reload]);
  const parse = useMemo(() => parseMetaJson(data?.currentMeta ?? null), [data?.currentMeta]);
  const functions = useMemo(() => getFunctions(parse.data), [parse.data]);

  if (state.status === "loading" && !data) {
    return (
      <div className="content">
        <LoadingState label="Carregando diagnóstico..." />
      </div>
    );
  }

  if (state.status === "error" && !data) {
    return (
      <div className="content">
        <ErrorState error={state.error} onRetry={reload} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="content">
        <ErrorState
          title="Sem dados"
          message="O backend nao devolveu payload de dashboard."
          onRetry={reload}
        />
      </div>
    );
  }

  const { device, currentMeta, latestSync, latestPorts } = data;
  const displayName = getDeviceDisplayName(data.device, parse.data);
  const isClaimed = !!device.currentWorkspaceId;
  const workspaceId = device.currentWorkspaceId ?? defaultWorkspaceId ?? null;
  const isOnlineNow = isDeviceOnlineNow(device);
  const latestSyncFreshness = getFreshness(latestSync?.measuredAt ?? null, SYNC_FRESHNESS_MS);
  const freshSync = latestSyncFreshness === "fresh" ? latestSync : null;
  const latestHeartbeatAt = latestSync?.measuredAt ?? device.lastSeenAt ?? null;
  const connectionState: ConnectionState = !isClaimed
    ? "unclaimed"
    : isOnlineNow
      ? "online"
      : "offline";
  const connectionText =
    connectionState === "online"
      ? "conectado"
      : connectionState === "offline"
        ? "offline"
        : "nao associado";
  const connectionColor =
    connectionState === "online"
      ? "var(--ok-600)"
      : connectionState === "offline"
        ? "var(--danger-600)"
        : "var(--ink-600)";
  const signalRows = buildSignalRows(functions, latestPorts, currentMeta?.configHashHex ?? null);
  const telemetryHealth = buildTelemetryHealthSummary(data.syncHistory ?? []);

  return (
    <>
      <PageHeader
        crumbs={[
          { label: "Dashboard", to: "/workflows" },
          { label: "Diagnóstico", to: "/devices" },
          { label: displayName },
        ]}
        title="Diagnóstico do gateway"
        meta={
          <>
            <span className="item">
              MQTT{" "}
              <b style={{ color: connectionColor }}>{connectionText}</b>
            </span>
            <span className="item">
              Firmware <b>{freshSync?.firmwareVersion ?? currentMeta?.firmwareVersion ?? "—"}</b>
            </span>
            <span className="item">
              Último visto <b>{formatRelative(device.lastSeenAt)}</b>
            </span>
          </>
        }
      />

      <div className="content">
        <DeviceHeaderCard
          displayName={displayName}
          rename={<DeviceRenameButton deviceId={device.id} nomeFantasia={device.nomeFantasia} onSaved={reload} />}
          mqttId={device.mqttId}
          isClaimed={isClaimed}
          connectionState={connectionState}
        />

        <section>
          <div className="section-h">
            <h2>
              Diagnóstico <span className="num">4</span>
            </h2>
            <div className="right">
              <span className="muted" style={{ fontSize: 12 }}>
                {freshSync
                  ? `Atualizado ${formatRelative(freshSync.measuredAt)}`
                  : `Sem heartbeat recente · ultimo ${formatRelative(latestHeartbeatAt)}`}
              </span>
            </div>
          </div>
          <div className="diag-grid" style={{ marginTop: 10 }}>
            <DiagCard
              icon={<IconBroadcast size={13} style={{ color: connectionIconColor(connectionState) }} />}
              label="Conexão MQTT"
              value={connectionState === "online" ? "Conectado" : connectionState === "offline" ? "Offline" : "Não associado"}
              sub={connectionDiagnosticSub(connectionState, latestHeartbeatAt)}
            />
            <DiagCard
              icon={<IconWifi size={13} style={{ color: rssiColor(freshSync?.rssi) }} />}
              label="RSSI"
              value={freshSync?.rssi != null ? `${freshSync.rssi}` : "—"}
              unit={freshSync?.rssi != null ? "dBm" : ""}
              sub={freshSync ? rssiLabel(freshSync.rssi) : staleSyncSub(latestSync?.measuredAt)}
            />
            <DiagCard
              icon={<IconHash size={13} style={{ color: "var(--brand-500)" }} />}
              label="IP do gateway"
              value={freshSync?.ip ?? "—"}
              mono
              sub={freshSync?.ip ? "rede local" : staleSyncSub(latestSync?.measuredAt)}
            />
            <DiagCard
              icon={<IconShield size={13} style={{ color: "var(--ink-500)" }} />}
              label="Firmware"
              value={freshSync?.firmwareVersion ?? currentMeta?.firmwareVersion ?? "—"}
              sub={
                currentMeta?.receivedAt
                  ? `meta ${formatRelative(currentMeta.receivedAt)}`
                  : "sem meta"
              }
            />
          </div>
        </section>

        <TelemetryHealthCard summary={telemetryHealth} />

        <section>
          <div className="section-h">
            <h2>
              Entradas digitais brutas{" "}
              <span className="num">{data.latestDigitalInputs?.length ?? 0}</span>
            </h2>
            <div className="right">
              <span className="muted" style={{ fontSize: 12 }}>
                leitura direta do GPIO antes da funcao
              </span>
            </div>
          </div>
          <DigitalInputsTable inputs={data.latestDigitalInputs ?? []} />
        </section>

        <section>
          <div className="section-h">
            <h2>
              Sinais recebidos <span className="num">{signalRows.length}</span>
            </h2>
            <div className="right">
              <button type="button" className="btn sm" disabled>
                <IconFilter size={12} /> Filtrar
              </button>
              <button type="button" className="btn sm" disabled>
                <IconDownload size={12} /> Exportar CSV
              </button>
            </div>
          </div>
          <SignalsTable rows={signalRows} />
        </section>

        <section>
          <div className="section-h">
            <h2>
              Configuração do gateway <span className="num">{functions.length} função(ões)</span>
            </h2>
            <div className="right">
              <span className="badge muted">schema · ghm.gateway.v2</span>
              <button type="button" className="btn sm" disabled>
                <IconEdit size={12} /> Editar funções
              </button>
            </div>
          </div>
          <FunctionsList functions={functions} />
          {currentMeta ? (
            <details className="acc" style={{ marginTop: 10 }}>
              <summary>
                <span>
                  <IconChevronRight size={12} className="caret" /> Ver JSON bruto do{" "}
                  <code>metaJson.functions</code>
                </span>
                <span className="muted">{currentMeta.metaJson.length} bytes</span>
              </summary>
              <pre>{formatJson(currentMeta.metaJson)}</pre>
            </details>
          ) : null}
        </section>

        <MaintenanceSection
          deviceId={device.id}
          mqttId={device.mqttId}
          workspaceId={workspaceId}
          defaultWorkspaceId={defaultWorkspaceId ?? "workspace-local"}
          onClaimed={reload}
        />
      </div>
    </>
  );
}

function rssiColor(rssi?: number | null): string {
  if (rssi == null) return "var(--ink-400)";
  if (rssi >= -55) return "var(--ok-500)";
  if (rssi >= -75) return "var(--warn-500)";
  return "var(--danger-500)";
}

function rssiLabel(rssi?: number | null): string {
  if (rssi == null) return "sem heartbeat";
  if (rssi >= -55) return "excelente · 4/4";
  if (rssi >= -67) return "bom · 3/4";
  if (rssi >= -75) return "médio · 2/4";
  if (rssi >= -85) return "fraco · 1/4";
  return "ruim · 0/4";
}

function formatJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

type ConnectionState = "online" | "offline" | "unclaimed";

function connectionIconColor(state: ConnectionState): string {
  if (state === "online") return "var(--ok-500)";
  if (state === "offline") return "var(--danger-500)";
  return "var(--ink-400)";
}

function connectionDiagnosticSub(state: ConnectionState, heartbeatAt?: string | null): string {
  if (state === "online") return `heartbeat recente · ${formatRelative(heartbeatAt)}`;
  if (state === "offline") return `sem sinal recente · ultimo ${formatRelative(heartbeatAt)}`;
  return "Faça o claim para associar";
}

function staleSyncSub(measuredAt?: string | null): string {
  if (!measuredAt) return "sem heartbeat";
  return `ultimo heartbeat ${formatRelative(measuredAt)}`;
}

// ─────────────────────────────────────────────────────────────────
// Sub-componentes locais
// ─────────────────────────────────────────────────────────────────

interface DeviceHeaderCardProps {
  displayName: string;
  rename: ReactNode;
  mqttId: string;
  isClaimed: boolean;
  connectionState: ConnectionState;
}

function DeviceHeaderCard({
  displayName,
  rename,
  mqttId,
  isClaimed,
  connectionState,
}: DeviceHeaderCardProps) {
  const connectionBadge =
    connectionState === "online"
      ? { className: "badge ok", label: "ONLINE" }
      : connectionState === "offline"
        ? { className: "badge danger", label: "OFFLINE" }
        : { className: "badge muted", label: "NAO ASSOCIADO" };

  return (
    <div className="dev-hd">
      <div className="dev-hd-l">
        <div className="dev-icon" aria-hidden>
          <IconCpu size={22} />
        </div>
        <div style={{ minWidth: 0 }}>
          <h1>
            {displayName}
            {rename}
            <span className={connectionBadge.className}>
              <span className={connectionState === "online" ? "led-anim" : "dot"} />{" "}
              {connectionBadge.label}
            </span>
            {isClaimed ? (
              <span className="badge brand">
                <span className="dot" /> CLAIMED
              </span>
            ) : (
              <span className="badge muted">
                <span className="dot" /> NÃO ASSOCIADO
              </span>
            )}
          </h1>
          <div className="id">
            <span>MQTT ID</span>
            <span className="truncate">{mqttId}</span>
            <button
              type="button"
              className="btn ghost sm"
              style={{ padding: "2px 6px" }}
              onClick={() => {
                navigator.clipboard?.writeText(mqttId);
              }}
              aria-label="Copiar MQTT ID"
            >
              <IconCopy size={11} />
            </button>
          </div>
        </div>
      </div>
      <div className="dev-hd-r">
        <button type="button" className="btn" disabled>
          <IconRefresh size={13} /> Resync
        </button>
        <button type="button" className="btn" disabled>
          <IconLink size={13} /> Reassociar
        </button>
        <button type="button" className="btn primary" disabled>
          <IconWrench size={13} /> Configurar
        </button>
      </div>
    </div>
  );
}

interface DiagCardProps {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  unit?: string;
  sub?: string;
  mono?: boolean;
}

function DiagCard({ icon, label, value, unit, sub, mono }: DiagCardProps) {
  return (
    <div className="diag-card">
      <div className="k">
        {icon}
        <span>{label}</span>
      </div>
      <div className={`v ${mono ? "mono" : ""}`}>
        {value}
        {unit ? <span className="u">{unit}</span> : null}
      </div>
      {sub ? <div className="sub">{sub}</div> : null}
    </div>
  );
}

interface TelemetryHealthCardProps {
  summary: TelemetryHealthSummary;
}

function TelemetryHealthCard({ summary }: TelemetryHealthCardProps) {
  const hasPoints = summary.points.length > 0;
  const statusText = !hasPoints
    ? "Sem amostras de sync nas últimas 24 horas."
    : summary.gapCount > 0
      ? `${summary.gapCount} quebra(s) acima de 10 min.`
      : "Linha contínua dentro do atraso tolerado.";
  const segments = hasPoints ? buildTelemetrySvgSegments(summary) : [];
  const lastPoint = summary.points.at(-1);

  return (
    <section className="telemetry-health" aria-label="Saúde da telemetria">
      <div className="section-h">
        <h2>
          Saúde da telemetria <span className="num">24h</span>
        </h2>
        <div className="right">
          <span className="muted" style={{ fontSize: 12 }}>
            {formatDateTime(summary.windowStart)} até {formatDateTime(summary.windowEnd)}
          </span>
        </div>
      </div>
      <div className="telemetry-card">
        <div className="telemetry-card__head">
          <strong>Linha de telemetria nas últimas 24 horas</strong>
          <span className={summary.gapCount > 0 ? "badge danger" : "badge ok"}>
            <span className="dot" /> {statusText}
          </span>
        </div>
        <svg
          className="telemetry-line"
          viewBox="0 0 100 18"
          role="img"
          aria-label={statusText}
          preserveAspectRatio="none"
        >
          <rect x="0" y="3" width="100" height="12" rx="2" className="telemetry-line__bg" />
          {segments.length === 0 && hasPoints ? (
            <line x1="0" x2="100" y1="9" y2="9" className="telemetry-line__ok" />
          ) : null}
          {segments.map((segment) => (
            <line
              key={`${segment.x1}-${segment.x2}-${segment.tone}`}
              x1={segment.x1}
              x2={segment.x2}
              y1="9"
              y2="9"
              className={`telemetry-line__${segment.tone}`}
            />
          ))}
          {lastPoint ? (
            <circle cx={timeToX(lastPoint.measuredAt, summary)} cy="9" r="1.15" className="telemetry-line__dot" />
          ) : null}
        </svg>
        <div className="telemetry-card__legend">
          <span><i className="ok" /> Normal até 5 min</span>
          <span><i className="warn" /> Oscilação entre 5 e 10 min</span>
          <span><i className="danger" /> Quebra acima de 10 min</span>
        </div>
      </div>
    </section>
  );
}

function buildTelemetrySvgSegments(summary: TelemetryHealthSummary) {
  return summary.segments.map((segment) => ({
    x1: timeToX(segment.startAt, summary),
    x2: timeToX(segment.endAt, summary),
    tone: segment.tone,
  }));
}

function timeToX(iso: string, summary: TelemetryHealthSummary): number {
  const start = Date.parse(summary.windowStart);
  const end = Date.parse(summary.windowEnd);
  const point = Date.parse(iso);
  if (!Number.isFinite(start) || !Number.isFinite(end) || !Number.isFinite(point) || end <= start) {
    return 0;
  }
  return Math.max(0, Math.min(100, ((point - start) / (end - start)) * 100));
}

interface SignalsTableProps {
  rows: TableRow[];
}

interface TableRow {
  key: string;
  port: string;
  name: string;
  mode: string;
  s: unknown;
  acc: unknown;
  v: unknown;
  vUnit: string;
  ts: string;
  freshness: ReturnType<typeof getFreshness>;
}

function buildSignalRows(
  functions: MetaFunctionEntry[],
  latestPorts: LatestPort[],
  currentConfigHash: string | null,
): TableRow[] {
  const byIndex = new Map<number, LatestPort>();
  for (const port of latestPorts) {
    if (typeof port.portIndex !== "number") continue;
    if (currentConfigHash && port.configHashHex && port.configHashHex !== currentConfigHash) {
      continue;
    }
    byIndex.set(port.portIndex, port);
  }

  return functions.flatMap((fn, index) => {
    const portIndex = typeof fn.p === "number" ? fn.p : null;
    const snap = portIndex !== null ? (byIndex.get(portIndex) ?? null) : null;
    const port = portIndex === null ? "—" : `P${portIndex}`;
    const baseTs = formatRelative(snap?.measuredAt ?? null);
    const freshness = getFreshness(snap?.measuredAt ?? null);
    const mode = normalizeMode(fn?.m ?? snap?.mode);

    const sMeta = fn ? getSignalMetadata(fn, "s") : {};
    const accMeta = fn ? getSignalMetadata(fn, "acc") : {};
    const vMeta = fn ? getSignalMetadata(fn, "v") : {};
    const baseLabel = fn?.label
      ? String(fn.label)
      : portIndex !== null
        ? `Entrada ${portIndex + 1}`
        : "—";

    const declared = fn?.values && !Array.isArray(fn.values) ? fn.values : null;
    const hasS = declared?.s !== undefined || snap?.s !== undefined;
    const hasAcc = declared?.acc !== undefined || snap?.acc !== undefined;
    const hasV = declared?.v !== undefined || snap?.v !== undefined;

    const entries: TableRow[] = [];
    const shared = { port, ts: baseTs, freshness };

    if (!hasS && !hasAcc && !hasV) {
      entries.push({
        ...shared,
        key: `fn-${index}-only`,
        name: baseLabel,
        mode,
        s: snap?.s,
        acc: snap?.acc,
        v: snap?.v,
        vUnit: vMeta.unit ?? "",
      });
    } else {
      if (hasS) {
        entries.push({
          ...shared,
          key: `fn-${index}-s`,
          name: `${baseLabel} — ${sMeta.label ?? "Estado"}`,
          mode: stateModeLabel(mode, fn),
          s: snap?.s,
          acc: undefined,
          v: undefined,
          vUnit: "",
        });
      }
      if (hasAcc) {
        entries.push({
          ...shared,
          key: `fn-${index}-acc`,
          name: `${baseLabel} — ${accMeta.label ?? "Total"}`,
          mode: accModeLabel(mode),
          s: undefined,
          acc: snap?.acc,
          v: undefined,
          vUnit: accMeta.unit ?? "",
        });
      }
      if (hasV) {
        entries.push({
          ...shared,
          key: `fn-${index}-v`,
          name: `${baseLabel} — ${vMeta.label ?? diagnosticValueKindLabel(vMeta.kind)}`,
          mode,
          s: undefined,
          acc: undefined,
          v: snap?.v,
          vUnit: vMeta.unit ?? "",
        });
      }
    }
    return entries.map((entry, i) => ({ ...entry, key: `${entry.key}-${index}-${i}` }));
  });
}

function SignalsTable({ rows }: SignalsTableProps) {
  return (
    <div className="tbl-wrap">
      <table className="tbl">
        <thead>
          <tr>
            <th>Porta</th>
            <th>Nome</th>
            <th>Modo</th>
            <th>
              Estado <span className="kbd">s</span>
            </th>
            <th>
              Acumulado <span className="kbd">acc</span>
            </th>
            <th>
              Leitura <span className="kbd">v</span>
            </th>
            <th>Medido em</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td className="port">{row.port}</td>
              <td className="name">{row.name}</td>
              <td>{row.mode || "—"}</td>
              <td>
                {row.freshness !== "fresh" ? (
                  <span className="badge muted">
                    <span className="dot" /> SEM SINAL
                  </span>
                ) : row.s === null || row.s === undefined ? (
                  <span className="muted">—</span>
                ) : (
                  <SignalStateBadge mode={row.mode} value={row.s} />
                )}
              </td>
              <td className="num-c">
                {row.freshness !== "fresh" ? (
                  <span className="muted">—</span>
                ) : row.acc === null || row.acc === undefined ? (
                  <span className="muted">—</span>
                ) : (
                  Number(row.acc).toLocaleString("pt-BR")
                )}
              </td>
              <td className="num-c">
                {row.freshness !== "fresh" ? (
                  <span className="muted">—</span>
                ) : row.v === null || row.v === undefined ? (
                  <span className="muted">—</span>
                ) : (
                  `${Number(row.v).toLocaleString("pt-BR", {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })} ${row.vUnit ?? ""}`.trim()
                )}
              </td>
              <td className="muted">
                {row.freshness === "fresh" ? row.ts : `${row.ts} · sem sinal`}
              </td>
            </tr>
          ))}
          {rows.length === 0 ? (
            <tr>
              <td colSpan={7} className="muted" style={{ textAlign: "center", padding: 18 }}>
                Nenhum sinal recebido ainda.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

interface DigitalInputsTableProps {
  inputs: LatestDigitalInput[];
}

function DigitalInputsTable({ inputs }: DigitalInputsTableProps) {
  return (
    <div className="tbl-wrap">
      <table className="tbl">
        <thead>
          <tr>
            <th>Entrada</th>
            <th>GPIO</th>
            <th>Nivel eletrico</th>
            <th>Logico</th>
            <th>Polaridade</th>
            <th>Medido em</th>
          </tr>
        </thead>
        <tbody>
          {inputs.map((input) => {
            const freshness = getFreshness(input.measuredAt ?? null);
            const isActive = Number(input.active) !== 0;
            const isHigh = Number(input.level) !== 0;
            return (
              <tr key={`${input.address}-${input.portIndex}`}>
                <td className="name">
                  {input.address.toUpperCase()} <span className="muted">P{input.portIndex}</span>
                </td>
                <td className="mono">GPIO{input.gpio}</td>
                <td>
                  {freshness !== "fresh" ? (
                    <span className="badge muted">
                      <span className="dot" /> SEM SINAL
                    </span>
                  ) : (
                    <span className={isHigh ? "badge muted" : "badge danger"}>
                      <span className="dot" /> {isHigh ? "HIGH" : "LOW"}
                    </span>
                  )}
                </td>
                <td>
                  {freshness !== "fresh" ? (
                    <span className="muted">-</span>
                  ) : (
                    <span className={isActive ? "badge ok" : "badge muted"}>
                      <span className="dot" /> {isActive ? "ATIVA" : "INATIVA"}
                    </span>
                  )}
                </td>
                <td>{input.polarity === "NC" ? "NC" : "NO"}</td>
                <td className="muted">
                  {freshness === "fresh"
                    ? formatRelative(input.measuredAt)
                    : `${formatRelative(input.measuredAt)} · sem sinal`}
                </td>
              </tr>
            );
          })}
          {inputs.length === 0 ? (
            <tr>
              <td colSpan={6} className="muted" style={{ textAlign: "center", padding: 18 }}>
                Aguardando firmware publicar diagnostico bruto das DIs.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function SignalStateBadge({ mode, value }: { mode: string; value: unknown }) {
  const modbusStatus = resolveModbusStatus(mode, value);
  if (modbusStatus) {
    const presentation = describeModbusStatus(modbusStatus);
    return (
      <span className={`badge ${presentation.tone}`}>
        <span className="dot" />
        {presentation.label}
      </span>
    );
  }

  const diagnostic = resolveAnalogDiagnostic(mode, value);
  if (diagnostic) {
    const presentation = describeAnalogDiagnostic(diagnostic);
    return (
      <span className={`badge ${presentation.tone}`}>
        <span className="dot" />
        {presentation.label}
      </span>
    );
  }

  return (
    <span
      className="badge"
      style={{
        background: truthy(value) ? "var(--ok-50)" : "var(--ink-50)",
        color: truthy(value) ? "var(--ok-600)" : "var(--ink-500)",
        borderColor: truthy(value) ? "var(--ok-100)" : "var(--border)",
      }}
    >
      <span className="dot" />
      {truthy(value) ? "ON" : "OFF"}
    </span>
  );
}

function truthy(v: unknown): boolean {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") {
    return v === "1" || v.toLowerCase() === "true" || v.toLowerCase() === "on";
  }
  return false;
}

function stateModeLabel(mode: string, fn: MetaFunctionEntry | null): string {
  if (mode === "DI" || mode === "LATCH") return "Contato seco";
  if (mode === "HSC") return "Direção";
  if (mode === "RST") return "Reset técnico";
  return fn?.m ? String(fn.m) : mode;
}

function accModeLabel(mode: string): string {
  if (mode === "CT") return "Pulso";
  if (mode === "PM") return "Pulso 1L";
  if (mode === "HSC") return "Contagem alta";
  return mode || "—";
}

interface FunctionsListProps {
  functions: MetaFunctionEntry[];
}

function FunctionsList({ functions }: FunctionsListProps) {
  if (functions.length === 0) {
    return (
      <div className="state">
        <h2 className="state__title">metaJson sem funções</h2>
        <p className="state__message">
          O gateway ainda não publicou nenhuma função em{" "}
          <code className="mono">metaJson.functions</code>.
        </p>
      </div>
    );
  }

  return (
    <div className="fn-list">
      {functions.map((fn, index) => {
        const port = typeof fn.p === "number" ? `P${fn.p}` : "P?";
        const mode = normalizeMode(fn.m);
        const kind = diagnosticFunctionKindLabel(fn);
        const unit = describeUnit(fn);
        const formulaTags = listFormulaTags(fn);
        return (
          <div key={fn.id ? String(fn.id) : `${port}-${index}`} className="fn-row">
            <div className="port-chip">{port}</div>
            <div className="label">
              <b>{fn.label?.trim() || `Entrada ${typeof fn.p === "number" ? fn.p + 1 : "?"}`}</b>
              <small title="Tags técnicas usadas em fórmulas">
                {formulaTags.length > 0 ? formulaTags.join(" · ") : "G0_P?"}
              </small>
            </div>
            <div className="meta">
              Modo: <b>{accModeLabel(mode) === "—" ? mode : accModeLabel(mode)}</b>
            </div>
            <div className="meta">
              Tipo: <b>{kind}</b>
              {unit ? ` · ${unit}` : ""}
            </div>
            <button type="button" className="btn ghost sm" disabled>
              <IconEdit size={12} /> Editar
            </button>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Devolve as tags públicas de fórmula que essa função expõe — uma por
 * sinal declarado em `metaJson.functions[].values`. Ex.: PM com s/acc/v
 * vira `["G0_P1_s", "G0_P1_acc", "G0_P1_v"]`. Quando o meta não declara
 * `values`, infere pelo modo (DI/LATCH → s; CT → acc; PM/HSC → acc+v).
 */
function listFormulaTags(fn: MetaFunctionEntry): string[] {
  if (typeof fn.p !== "number") return [];
  const base = `G0_P${fn.p}`;
  const values = fn.values;

  if (values && !Array.isArray(values)) {
    return (["s", "acc", "v"] as const)
      .filter((signal) => values[signal] !== undefined)
      .map((signal) => `${base}_${signal}`);
  }

  const mode = normalizeMode(fn.m);
  if (mode === "DI" || mode === "LATCH") return [`${base}_s`];
  if (mode === "RST") return [`${base}_s`];
  if (mode === "CT") return [`${base}_acc`];
  if (mode === "PM" || mode === "HSC") return [`${base}_acc`, `${base}_v`];
  return [base];
}

function describeUnit(fn: MetaFunctionEntry): string {
  const values = fn.values;
  if (!values || Array.isArray(values)) return "";
  const accUnit = values.acc?.unit;
  const vUnit = values.v?.unit;
  if (accUnit && vUnit) return `${accUnit} · ${vUnit}`;
  return accUnit || vUnit || "";
}

interface MaintenanceSectionProps {
  deviceId: string;
  mqttId: string;
  workspaceId: string | null;
  defaultWorkspaceId: string;
  onClaimed: () => void;
}

function MaintenanceSection({
  deviceId,
  mqttId,
  workspaceId,
  defaultWorkspaceId,
  onClaimed,
}: MaintenanceSectionProps) {
  const { permissoes } = useAuth();
  const [open, setOpen] = useState(false);
  const [removing, setRemoving] = useState(false);

  const handleRemoveFromWorkflow = async () => {
    if (
      typeof window !== "undefined" &&
      !window.confirm(
        "Remover este dispositivo do workflow? Ele volta para 'descobertos' e pode ser associado de novo. Os widgets desta associação serão perdidos.",
      )
    ) {
      return;
    }
    setRemoving(true);
    try {
      await unclaimDevice(deviceId);
      onClaimed(); // recarrega — o device sai do workspace
    } catch (error) {
      if (typeof window !== "undefined") {
        window.alert(
          `Falha ao remover do workflow: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    } finally {
      setRemoving(false);
    }
  };

  return (
    <section>
      <div className="section-h">
        <h2>Manutenção</h2>
        <div className="right">
          <span className="muted" style={{ fontSize: 12 }}>
            {permissoes.gerenciarDispositivos
              ? "Operações sensíveis · requer permissão"
              : "Operações sensíveis · seu papel é somente leitura"}
          </span>
        </div>
      </div>
      <div className="maint">
        <CopyableRow label="Device ID" full={deviceId} display={truncate(deviceId, 22)} />
        <CopyableRow label="MQTT ID" full={mqttId} display={truncate(mqttId, 22)} />
        <div className="maint-row">
          <div className="k">Workspace atual</div>
          <div className="v">{workspaceId ?? "—"}</div>
          {permissoes.gerenciarDispositivos ? (
            <>
              <button type="button" className="btn sm" onClick={() => setOpen(true)}>
                <IconLink size={12} /> Reassociar
              </button>
              {workspaceId ? (
                <button
                  type="button"
                  className="btn sm ghost"
                  onClick={handleRemoveFromWorkflow}
                  disabled={removing}
                  title="Desassocia o dispositivo deste workflow"
                >
                  {removing ? "Removendo..." : "Remover do workflow"}
                </button>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
      {open ? (
        <ReassociateModal
          deviceId={deviceId}
          defaultWorkspaceId={workspaceId ?? defaultWorkspaceId}
          onClose={() => setOpen(false)}
          onDone={() => {
            setOpen(false);
            onClaimed();
          }}
        />
      ) : null}
    </section>
  );
}

interface CopyableRowProps {
  label: string;
  display: string;
  full: string;
}

function CopyableRow({ label, display, full }: CopyableRowProps) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="maint-row">
      <div className="k">{label}</div>
      <div className="v" title={full}>
        {display}
      </div>
      <button
        type="button"
        className="btn ghost sm"
        onClick={() => {
          navigator.clipboard?.writeText(full);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        }}
      >
        <IconCopy size={12} />
        {copied ? "Copiado" : "Copiar"}
      </button>
    </div>
  );
}

function truncate(value: string, max: number): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}…`;
}

interface ReassociateModalProps {
  deviceId: string;
  defaultWorkspaceId: string;
  onClose: () => void;
  onDone: () => void;
}

type SubmitState =
  | { status: "idle"; message: null }
  | { status: "submitting"; message: null }
  | { status: "success"; message: string }
  | { status: "error"; message: string };

function ReassociateModal({
  deviceId,
  defaultWorkspaceId,
  onClose,
  onDone,
}: ReassociateModalProps) {
  const [workspaceId, setWorkspaceId] = useState(defaultWorkspaceId);
  const [claimedByUserId, setClaimedByUserId] = useState("local-user");
  const [state, setState] = useState<SubmitState>({ status: "idle", message: null });
  const modalRef = useModalA11y({ open: true, onClose });

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!workspaceId.trim() || !claimedByUserId.trim()) {
      setState({ status: "error", message: "Informe workspaceId e claimedByUserId." });
      return;
    }
    setState({ status: "submitting", message: null });
    try {
      await claimDevice({
        deviceId,
        workspaceId: workspaceId.trim(),
        claimedByUserId: claimedByUserId.trim(),
      });
      setState({
        status: "success",
        message: `Reassociado ao workflow ${workspaceId.trim()}.`,
      });
      setTimeout(onDone, 400);
    } catch (error) {
      setState({
        status: "error",
        message: error instanceof Error ? error.message : "Falha na reassociação.",
      });
    }
  }

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reassoc-title"
        ref={modalRef}
        tabIndex={-1}
      >
        <header className="modal__header">
          <div>
            <p className="page__eyebrow">Manutenção</p>
            <h2 id="reassoc-title">Reassociar dispositivo</h2>
          </div>
          <button type="button" className="btn ghost sm" onClick={onClose}>
            Fechar
          </button>
        </header>
        <form className="modal__body" onSubmit={handleSubmit}>
          <label className="field">
            <span className="field__label">workspaceId</span>
            <input
              className="input field__control mono"
              value={workspaceId}
              onChange={(event) => setWorkspaceId(event.target.value)}
              disabled={state.status === "submitting"}
            />
          </label>
          <label className="field">
            <span className="field__label">claimedByUserId</span>
            <input
              className="input field__control mono"
              value={claimedByUserId}
              onChange={(event) => setClaimedByUserId(event.target.value)}
              disabled={state.status === "submitting"}
            />
          </label>
          {state.message ? (
            <div
              className={state.status === "error" ? "inline-warning" : "inline-success"}
              role="status"
            >
              {state.message}
            </div>
          ) : null}
          <div className="modal__actions">
            <button type="button" className="btn ghost" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn primary"
              disabled={state.status === "submitting"}
            >
              {state.status === "submitting" ? "Reassociando..." : "Reassociar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
